/**
 * Basic Information Section
 * Package code, name, description, active status, sort order
 */

import { UseFormReturn } from 'react-hook-form';
import { PackageFormValues } from '@/src/schemas/admin/package-schema';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';

interface BasicInfoSectionProps {
  form: UseFormReturn<PackageFormValues>;
  isEditMode: boolean;
}

export default function BasicInfoSection({ form, isEditMode }: BasicInfoSectionProps) {
  return (
    <div className="grid gap-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Package Code */}
        <FormField
          control={form.control}
          name="package_code"
          render={({ field }) => (
            <FormItem>
              <FormLabel>รหัสแพ็กเกจ *</FormLabel>
              <FormControl>
                <Input
                  placeholder="starter, basic, premium"
                  {...field}
                  disabled={isEditMode}
                  className={isEditMode ? 'bg-muted' : ''}
                />
              </FormControl>
              <FormDescription>
                ใช้ตัวอักษรเล็ก ตัวเลข และ _ - เท่านั้น (ไม่สามารถแก้ไขได้)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Package Name */}
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <FormLabel>ชื่อแพ็กเกจ *</FormLabel>
              <FormControl>
                <Input placeholder="Starter Package" {...field} />
              </FormControl>
              <FormDescription>
                ชื่อแพ็กเกจที่จะแสดงให้ลูกค้าเห็น
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      {/* Description */}
      <FormField
        control={form.control}
        name="description"
        render={({ field }) => (
          <FormItem>
            <FormLabel>คำอธิบาย *</FormLabel>
            <FormControl>
              <Textarea
                placeholder="อธิบายรายละเอียดและประโยชน์ของแพ็กเกจ..."
                className="resize-none"
                rows={3}
                {...field}
                value={field.value || ''}
              />
            </FormControl>
            <FormDescription>
              กรุณากรอกคำอธิบายแพ็กเกจ (ไม่เกิน 500 ตัวอักษร)
            </FormDescription>
            <FormMessage />
          </FormItem>
        )}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Sort Order */}
        <FormField
          control={form.control}
          name="sort_order"
          render={({ field }) => (
            <FormItem>
              <FormLabel>ลำดับการแสดงผล *</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  {...field}
                  onChange={(e) => field.onChange(parseInt(e.target.value) || 0)}
                />
              </FormControl>
              <FormDescription>
                ลำดับในการแสดงผล (0-100, ยิ่งมากแสดงก่อน)
              </FormDescription>
              <FormMessage />
            </FormItem>
          )}
        />

        {/* Is Active */}
        <FormField
          control={form.control}
          name="is_active"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">เปิดใช้งาน</FormLabel>
                <FormDescription>
                  เปิด/ปิดการแสดงแพ็กเกจนี้
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
